/**
 * Frontend capacity helpers — mirror backend `_make_capacity_status` so any
 * batch object (even one fetched before the backend started attaching
 * `capacity_status`) renders consistently.
 *
 * State levels:
 *   below  → < 90 %  (green / default)
 *   near   → 90–99 % (amber "Near full")
 *   full   → exactly capacity (red "Full")
 *   over   → exceeds capacity (red "Over capacity")
 *   unset  → batch has no capacity configured
 */
export const NEAR_FULL_THRESHOLD = 0.75;

export function getCapacityStatus(batch) {
  if (!batch) return null;
  if (batch.capacity_status) return batch.capacity_status;
  const enrolled = Math.max(0, Number(batch.enrolled_count || 0));
  const capacity = Math.max(0, Number(batch.capacity || 0));
  if (capacity <= 0) {
    return { enrolled, capacity, percent: 0, state: "unset", is_full: false, is_near_full: false, can_enroll: true };
  }
  const percent = Math.round((enrolled / capacity) * 1000) / 10;
  let state = "below";
  if (enrolled > capacity) state = "over";
  else if (enrolled >= capacity) state = "full";
  else if (enrolled / capacity >= NEAR_FULL_THRESHOLD) state = "near";
  return {
    enrolled,
    capacity,
    percent,
    state,
    is_full: state === "full" || state === "over",
    is_near_full: state === "near",
    can_enroll: (batch.status || "active") === "active",
  };
}

/** Tailwind classes for the inline `enrolled/capacity Students` text. */
export function capacityTextClass(status) {
  if (!status || status.state === "unset") return "text-slate-400";
  if (status.state === "over") return "text-red-700 font-semibold";
  if (status.state === "full") return "text-red-600 font-medium";
  if (status.state === "near") return "text-amber-600 font-medium";
  return "text-slate-500";
}

/**
 * Returns a label for the right-aligned pill on a batch picker entry.
 * `null` when nothing should be shown (below-threshold or unset).
 */
export function capacityBadge(status) {
  if (!status) return null;
  // Over capacity → strong solid red so the breach is unmistakable.
  if (status.state === "over") return { label: "Over Capacity", cls: "bg-red-600 text-white" };
  if (status.state === "full") return { label: "Full", cls: "bg-red-100 text-red-700" };
  if (status.state === "near") return { label: "Near Full", cls: "bg-amber-100 text-amber-700" };
  return null;
}

/** Human label "12 / 20 Students" — always renders, even when capacity is unset. */
export function capacityLabel(status) {
  if (!status) return "";
  if (status.state === "unset") return `${status.enrolled} Students`;
  return `${status.enrolled}/${status.capacity} Students`;
}
